
import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

import Modal, { ModalBody, ModalFooter } from 'Components/Modal';
import { Grid, List, ListItem, ListItemText } from '@mui/material';
import { Button, Stepper, InputSearch, Form, Input } from 'Components';
import { NotificationManager } from 'Components/Notifications';
import { LogManagerConsole } from 'Core/logManager';
import { validateJsonObject } from 'Core/data/Helpers';

import Step0 from './components/step0'
import Step1 from './components/step1'
import Step2 from './components/step2'
import Step3 from './components/step3'
import Step4 from './components/step4'

//actions
import {
    alertsWizardClose,
    alertsWizardSave,
    catalogLoad,
    alertsWizardCleanFields,
    clearRedux
} from 'Redux/actions';

const INIT_STATE = {
    name: "",
    severity: 'Low',
    subscribers: [],
    assingEntity: "All",
    config: {},
    assignedTo: [],
    schedule: null
}

// Styles
import './index.scss';

const WizardAlert = (props) => {

    const dispatch = useDispatch()
    const { messages } = useIntl()
    const formRef = useRef()

    const { onResult } = props
    const { loading, isOpenModal, alertLoad, response } = useSelector(state => state.alertsWizardRedux)

    const [currentStep, setCurrentStep] = useState(0)
    const [currentAlert, setCurrentAlert] = useState()
    const [configuration, setConfiguration] = useState(INIT_STATE);

    const maxStep = currentAlert?.code == "RouteWatchers" ? 3 : 4;

    useEffect(() => {
        dispatch(catalogLoad(["contacts"]));
    }, []);
    
    useEffect(() => {
        if(currentStep==0 && !configuration?.id){
            setCurrentAlert()
            setConfiguration(INIT_STATE)
        }
    }, [configuration]);


    useEffect(() => {
        if (alertLoad) {
            dispatch(clearRedux("ALERTS_WIZARD", { alertLoad: null }))

            LogManagerConsole("WizardAlert", "alertLoad", alertLoad)
            let configJSON = alertLoad?.configJSON ? validateJsonObject(alertLoad.configJSON) : {}
            let newValues = { config: configJSON, ...alertLoad }

            newValues.assingEntity = newValues.assignedTo?.length > 0 && newValues.assignedTo[0].entity?.name || "Unit"
            newValues.assignedTo = newValues.assignedTo?.map(x => x.entity)

            setCurrentAlert({ id: alertLoad.id, type: alertLoad.type })
            setConfiguration(newValues)
        }
    }, [alertLoad])

    useEffect(() => {
        if (response) {
            if (response.result) {
                NotificationManager.success(
                    messages[configuration.id ? 'alertsNewAlert_editedMessage' : 'alertsNewAlert_createdMessage'],
                    messages['unit_validation_success_title'],
                    3000,
                    null,
                    null,
                    'success',
                    null
                );

                close(true)
            } else {
                setCurrentStep(currentStep - 1)
                const errorMessage = messages['alertsNewAlert_' + response.error?.error?.replace(/ /g, "_")] || response.error?.error;
                NotificationManager.error(
                    errorMessage || messages['alertsNewAlert_ErrorMessage'],
                    messages['alertsNewAlert_titleErrorMessage'],
                    3000,
                    null,
                    null,
                    'error',
                    null
                );
            }

            dispatch(clearRedux("ALERTS_WIZARD", { response: null }))
        }
    }, [response])

    const close = useCallback((refresh) => {
        setCurrentStep(0)
        setCurrentAlert()
        setConfiguration(INIT_STATE)
        dispatch(alertsWizardClose())
        dispatch(clearRedux("ALERTS_WIZARD"))
        dispatch(alertsWizardCleanFields(true))

        if (onResult)
            onResult(refresh)
    })

    const nextStep = useCallback(() => {

        if (currentStep == maxStep) {

            if(configuration?.schedule?.frequency === "Custom" && configuration?.schedule?.customSchedule?.length === 0){
                return NotificationManager.warning(
                    messages["alertsNewAlert_noRangeSelected"],
                    messages['alertsNewAlert_warning'],
                    3000,
                    null,
                    null,
                    'warning',
                    null
                );
            }
            let config = configuration.config
            delete (config.__typename)
            delete (config.parentId)
            const dataToSave = {
                id: configuration.id,
                name: configuration.name,
                type: configuration.type || (currentAlert.code === 'Inspection' ? 'Inspection' : currentAlert.code),
                assignedTo: configuration?.assignedTo?.map(x => {
                    return {
                        "id": null,
                        "entity": {
                            "id": x.id,
                            "name": x.name
                        }
                    }
                }),
                config: JSON.stringify(config),
                severity: configuration.severity,
                subscribers: configuration.subscribers.map(x => {
                    return {
                        "id": null,
                        "contactId": x?.contactId || 0,
                        "alert": true,
                        "alertBefore": false,
                        "alertAfter": false,
                        "type": x?.type || null,
                    }
                }) || [],
                triggers: configuration.triggersRaw?.map(x => {
                    return {
                        id: null,
                        entity: { id: x.id, name: x.name, label: x.label },
                    }
                }) || [],

                schedule: configuration.schedule && {
                    id: configuration.schedule.id,
                    frequency: configuration.schedule.frequency,
                    timeFrom: configuration.schedule.timeFrom,
                    timeTo: configuration.schedule.timeTo,
                    occurrences: configuration.schedule.occurrences,
                    occurrencesLimitPerDay: configuration.schedule.occurrencesLimitPerDay,
                    afterConsecutiveEvents: configuration.schedule.afterConsecutiveEvents,
                    customSchedule: configuration.schedule.customSchedule.map(x => {
                        return {
                            day: x.day,
                            timeTo: x.timeTo,
                            timeFrom: x.timeFrom
                        }
                    })
                }
            }

            LogManagerConsole("WizardAlert", "save", dataToSave, configuration)
            setCurrentStep(currentStep + 1)
            dispatch(alertsWizardSave(dataToSave))
        }
        else
            setCurrentStep(currentStep + 1)

    })

    return (
        <Modal
            title={configuration.id ? messages['alertsNewAlert_EditTitle'] : messages['alertsNewAlert_title']}
            open={isOpenModal}
            size='medium'
            position='right'
            header={false}
            loading={loading}
            handleClose={() => close()}
            className="alerts"
        >
            <ModalBody className='mb-200 container-alerts'>

                <span className='title-alert'>
                    {
                        configuration.id
                            ? `${messages['alertsNewAlert_EditTitle']} ${currentAlert && messages['alertsNewAlert_' + currentAlert.code] || ''}`
                            : `${messages['alertsNewAlert_title']} ${currentStep != 0 ? currentAlert && messages['alertsNewAlert_' + currentAlert.code] || '' : ''}`
                    }
                </span>

                {
                    currentStep == 0 &&
                    <Step0 currentAlert={currentAlert} setCurrentAlert={setCurrentAlert} setCurrentStep={setCurrentStep} />
                }
                {
                    currentStep > 0 &&
                    <Grid
                        container
                        justifyContent={'center'}
                        direction={'column'}
                    >
                        <Grid item>
                            <Stepper
                                activeStep={currentStep - 1}
                                steps={
                                    maxStep == 3 
                                    ?
                                    [messages['alertsNewAlert_generalTab'],
                                    messages['alertsNewAlert_schedulingFrequencyTab'],
                                    messages['alertsNewAlert_subscribersTab']]
                                    :
                                    [messages['alertsNewAlert_generalTab'],
                                    messages['alertsNewAlert_schedulingFrequencyTab'],
                                    messages['alertsNewAlert_subscribersTab'],
                                    messages['alertsNewAlert_assignUnitsTab']]
                                }
                            />
                            <div style={{ margin: '0 25px 0px 40px', maxWidth: '630px' }}>
                                {
                                    currentStep == 1 &&
                                    <Step1 formRef={formRef} nextStep={nextStep} currentAlert={currentAlert} setCurrentAlert={setCurrentAlert} value={configuration} onChange={setConfiguration} />
                                }
                                {
                                    currentStep == 2 &&
                                    <Step2 formRef={formRef} nextStep={nextStep} currentAlert={currentAlert} value={configuration} onChange={setConfiguration} />
                                }
                                {
                                    currentStep == 3 &&
                                    <Step3 formRef={formRef} nextStep={nextStep} currentAlert={currentAlert} value={configuration} onChange={setConfiguration} />
                                }
                                {
                                    currentStep > 3 &&
                                    <Step4 formRef={formRef} nextStep={nextStep} currentAlert={currentAlert} value={configuration} onChange={setConfiguration} />
                                }
                            </div>
                        </Grid>
                    </Grid>
                }

            </ModalBody>
            <ModalFooter>
                <Button
                    className='btn-gray'
                    onClick={() => close()}
                >
                    {messages['alertsNewAlert_buttonCancel']}
                </Button>
                {
                    currentStep > 0 &&
                    <Button
                        disabled={currentStep == 1 && configuration.id}
                        className='btn-cancel'
                        onClick={() => {
                            if (currentStep == 1) {
                                setCurrentAlert()
                                setConfiguration(INIT_STATE)
                                dispatch(alertsWizardCleanFields(true))
                            }
                            setCurrentStep(currentStep - 1)
                        }}
                    >
                        {messages['alertsNewAlert_buttonBack']}
                    </Button>
                }
                {
                    currentStep > 0 &&
                    <Button
                        //disabled={!type}
                        className="btn-blue"
                        onClick={(event) => {
                            event.preventDefault();
                            formRef.current.submit();
                        }}
                    >
                        {
                            currentStep === maxStep ?
                                configuration.id ? messages['alertsNewAlert_editButton'] : messages['alertsNewAlert_addNewButton']
                                : messages['alertsNewAlert_buttonNext']
                        }

                    </Button>
                }
            </ModalFooter>
        </Modal>
    )
}

export default WizardAlert;